import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const DIALOG_CSS = `
dialog.ic-top-dialog, dialog.ic-confirm-dialog {
  position: fixed;
  inset: 0;
  z-index: 400;
  width: 100vw;
  height: 100dvh;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: none;
  overflow: auto;
}
dialog.ic-top-dialog { background: #1A202C; color: #fff; }
dialog.ic-top-dialog::backdrop { background: #1A202C; }
dialog.ic-confirm-dialog { background: rgba(26, 32, 44, 0.82); }
dialog.ic-confirm-dialog::backdrop { background: rgba(26, 32, 44, 0.82); }
`;

function useTopLayerDialog(open: boolean) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;

    const openDialog = () => {
      if (typeof el.showModal !== 'function') {
        el.setAttribute('open', '');
        return;
      }
      try {
        if (!el.open) el.showModal();
      } catch {
        if (!el.open) el.setAttribute('open', '');
      }
    };

    openDialog();
    return () => {
      if (el.open && typeof el.close === 'function') el.close();
    };
  }, [open]);

  return ref;
}

/**
 * Viewport dialog in the browser top layer (showModal), portaled to document.body.
 * A transformed wizard step cannot keep this preview inside its box, so the
 * review screen underneath stays covered. Escape closes via onClose.
 */
export function TopLayerDialog({
  open,
  onClose,
  label,
  variant,
  testId,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  variant: 'preview' | 'confirm';
  testId?: string;
  children: React.ReactNode;
}) {
  const ref = useTopLayerDialog(open);
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <style>{DIALOG_CSS}</style>
      <dialog
        ref={ref}
        className={variant === 'preview' ? 'ic-top-dialog' : 'ic-confirm-dialog'}
        aria-label={label}
        data-testid={testId}
        onCancel={(event) => {
          event.preventDefault();
          onClose();
        }}
      >
        {children}
      </dialog>
    </>,
    document.body,
  );
}
